import { Cell, Label, Pie, PieChart } from "recharts"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/common/chart"

// ---------------------------------------------------------------------------
// StatusDonutChart — DYNAMIC: colors + names from API
// ---------------------------------------------------------------------------

export interface StatusDonutChartProps {
  data: { title: string; count: number; fill: string }[]
  total: number
  translate: (key: string) => string
  isHebrew: boolean
  centerLabel?: string
  onSliceClick?: (entry: { title: string; count: number; fill: string }, index: number) => void
}

export function StatusDonutChart({
  data,
  total,
  translate,
  isHebrew,
  centerLabel,
  onSliceClick,
}: StatusDonutChartProps) {
  const config: ChartConfig = {
    count: { label: centerLabel, color: "hsl(var(--chart-1))" },
  }
  data.forEach((d) => {
    config[d.title] = { label: d.title, color: d.fill }
  })

  return (
    <ChartContainer
      config={config}
      className="mx-auto h-[280px] w-full"
      dir={isHebrew ? "rtl" : "ltr"}
    >
      <PieChart>
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              formatter={(value, name) => {
                const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0
                return [`${Number(value).toLocaleString()} (${pct}%)`, name]
              }}
            />
          }
        />
        <Pie
          data={data}
          dataKey="count"
          nameKey="title"
          innerRadius="42%"
          outerRadius="68%"
          strokeWidth={3}
          stroke="white"
          className="cursor-pointer"
          aria-label="Status distribution donut chart"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.fill}
              onClick={onSliceClick ? () => onSliceClick(entry, index) : undefined}
              style={onSliceClick ? { cursor: "pointer" } : undefined}
            />
          ))}
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) - 8}
                      className="fill-foreground"
                      style={{ fontSize: 28, fontWeight: 700, fontFamily: "ui-monospace, monospace" }}
                    >
                      {total.toLocaleString()}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 16}
                      className="fill-muted-foreground"
                      style={{ fontSize: 11 }}
                    >
                      {centerLabel || translate("Total")}
                    </tspan>
                  </text>
                )
              }
            }}
          />
        </Pie>
        <ChartLegend className="flex flex-wrap gap-2 mt-2" content={<ChartLegendContent />} />
      </PieChart>
    </ChartContainer>
  )
}
